import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Footer from './Footer';
import '../styles/Terms.css';
// Optional: import Footer if you want the global footer on this page
// import Footer from '../components/Footer';

export default function PaymentTerms() {

const heroRef = useRef(null);
const wrapRef = useRef(null);
const tocRef  = useRef(null);



    /////Sticky TOC/////
      useEffect(() => {
    const header = document.querySelector('.header'); // your fixed header, adjust selector
    const getHeaderH = () => (header?.offsetHeight || 94);

    const syncFixedMetrics = () => {
        if (!tocRef.current || !wrapRef.current) return;
        const wrapRect = wrapRef.current.getBoundingClientRect();
        tocRef.current.style.setProperty('--toc-left', `${wrapRect.left + window.scrollX}px`);
        tocRef.current.style.setProperty('--toc-width', '280px'); // or keep the measured width if you prefer
        tocRef.current.style.setProperty('--sticky-top', `${getHeaderH() + 16}px`);
        };

    const onScroll = () => {
            // --- bottom cap so TOC stops at end of terms content ---
    const stickyTop = getHeaderH() + 16;
    const wrapRect = wrapRef.current.getBoundingClientRect();
    const tocH = tocRef.current.offsetHeight;

    // where the TOC's bottom sits right now (page coords)
    const currentBottom = window.scrollY + stickyTop + tocH;
    // where the terms content ends (page coords); or use '#contact' if you prefer
    const boundaryBottom =
    (document.getElementById('contact')?.getBoundingClientRect().bottom ?? wrapRect.bottom) + window.scrollY;

    if (currentBottom > boundaryBottom) {
    tocRef.current.classList.add('is-capped');
    tocRef.current.style.setProperty('--cap-y', `${boundaryBottom - currentBottom}px`); // negative translate
    syncFixedMetrics(); // keep left/width synced while capped
    } else {
    tocRef.current.classList.remove('is-capped');
    tocRef.current.style.removeProperty('--cap-y');
    }


      if (!heroRef.current || !tocRef.current) return;
      // only manage on tablet/desktop
      if (window.innerWidth < 769) {
        tocRef.current.classList.remove('is-fixed');
        tocRef.current.style.removeProperty('--toc-left');
        tocRef.current.style.removeProperty('--toc-width');
        return;
      }
      const heroBottom = heroRef.current.getBoundingClientRect().bottom;
      if (heroBottom <= getHeaderH() + 16) {
        tocRef.current.classList.add('is-fixed');
        syncFixedMetrics();
      } else {
        tocRef.current.classList.remove('is-fixed');
      }
    };

    const onResize = () => { onScroll(); };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    // initial pass
    onScroll();

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
    };
  }, []);



  useEffect(() => { window.scrollTo(0, 0); }, []);
  const lastUpdated = 'August 12, 2025';

  return (
    <main className="lenis-content terms-page" aria-labelledby="terms-title">
      {/* Hero */}
      <section ref={heroRef} className="terms-hero">
        <div className="terms-hero__content">
          <h1 id="terms-title" className="terms-hero__title">Payment Terms & Conditions</h1>
          <p className="terms-hero__subtitle">
            A baseline set of terms for commercial transactions involving industrial equipment—designed to
            be clear, fair, and practical.
          </p>
        </div>
        <div className="terms-hero__overlay" aria-hidden="true" />
      </section>

      {/* Body */}
      <section ref={wrapRef} className="terms-wrap">
        {/* On‑this‑page / ToC */}
        <aside ref={tocRef} className="terms-toc" aria-label="On this page">
          <div className="toc-card">
            <h2 className="toc-title">On this page</h2>
            <nav className="toc-nav">
              <a href="#scope">1. Scope & Applicability</a>
              <a href="#quotes">2. Quotes & Pricing</a>
              <a href="#payment">3. Payment Methods & Timing</a>
              <a href="#invoicing">4. Invoicing & Documentation</a>
              <a href="#taxes">5. Taxes</a>
              <a href="#credit">6. Purchase Orders & Credit Terms</a>
              <a href="#late">7. Late Payments & Remedies</a>
              <a href="#title">8. Title & Risk of Loss</a>
              <a href="#inspection">9. Inspection & Acceptance</a>
              <a href="#deposits">10. Deposits & Cancellations</a>
              <a href="#offset">11. Setoff</a>
              <a href="#returns">12. Returns & Refunds</a>
              <a href="#fm">13. Force Majeure</a>
              <a href="#compliance">14. Compliance</a>
              <a href="#law">15. Governing Law & Disputes</a>
              <a href="#changes">16. Changes to these Terms</a>
              <a href="#contact">17. Payment Instructions & Contact</a>
            </nav>
          </div>
        </aside>

        {/* Terms content */}
        <article className="terms-body">
          <p className="terms-updated">
            Last updated: {lastUpdated}. <span className="legal-note">These Terms are provided for general
            information and are not legal advice. Please have your counsel review for your specific
            operations and jurisdictions.</span>
          </p>

          <section id="scope" className="terms-section">
            <h2>1. Scope & Applicability</h2>
            <p>
              These Payment Terms & Conditions ("Terms") apply to commercial transactions for industrial
              equipment, parts, and related services conducted by our company ("Company"). They cover both
              (a) transactions where we purchase equipment from a seller ("you"), and (b) transactions where
              we sell equipment or services to a business customer (also "you"). If we have a signed master
              agreement or a written quote/contract that conflicts with these Terms, the signed document
              controls only for the conflicting term.
            </p>
          </section>

          <section id="quotes" className="terms-section">
            <h2>2. Quotes & Pricing</h2>
            <ul>
              <li>
                <strong>Quotes for our purchases.</strong> When we offer to buy your equipment, our written
                quote is based on information you provide and is subject to verification upon inspection. Unless
                stated otherwise in writing, quotes are valid for 15 days and may be adjusted for condition,
                quantity, or market movement observed at inspection.
              </li>
              <li>
                <strong>Quotes for our sales.</strong> Prices for equipment or services we sell exclude taxes,
                freight, rigging, permits, and third‑party charges unless expressly included. Stated lead times
                are estimates.
              </li>
              <li>
                <strong>Errors.</strong> We reserve the right to correct typographical or clerical errors in
                quotes, order acknowledgements, and invoices.
              </li>
            </ul>
          </section>

          <section id="payment" className="terms-section">
            <h2>3. Payment Methods & Timing</h2>
            <ul>
              <li>
                <strong>Accepted methods.</strong> ACH and wire transfer are preferred. Company checks may be
                accepted with prior approval. Card payments, if offered, may include a processing surcharge.
              </li>
              <li>
                <strong>When we purchase from you.</strong> Payment timing is as stated in our written quote
                or purchase agreement. If not stated, payment is made promptly after we accept the equipment
                following inspection and receipt of the required documentation (see Section 4). Payment can be
                made at pickup, at delivery, or after acceptance at our facility, depending on the logistics
                agreed in writing.
              </li>
              <li>
                <strong>When we sell to you.</strong> Terms are as stated on our invoice or order
                acknowledgement. If credit terms are approved, standard terms are Net 30 from invoice date
                unless otherwise stated. Otherwise, payment in full may be required prior to shipment or
                pickup.
              </li>
            </ul>
          </section>

          <section id="invoicing" className="terms-section">
            <h2>4. Invoicing & Documentation</h2>
            <ul>
              <li>
                <strong>For our purchases.</strong> You agree to provide a signed bill of sale, proof of
                ownership and authority to sell, serial/asset lists, and any required lien releases or tax forms
                (e.g., W‑9 in the U.S.). We may withhold payment until documentation is complete.
              </li>
              <li>
                <strong>For our sales.</strong> Invoices will reference item, quantity, serials (if
                applicable), ship‑to details, taxes, freight (if prepaid), and payment instructions. Disputes on
                invoices must be raised in writing within 5 business days of receipt.
              </li>
            </ul>
          </section>

          <section id="taxes" className="terms-section">
            <h2>5. Taxes</h2>
            <p>
              Prices exclude sales, use, excise, VAT/GST, and similar taxes unless expressly stated. For our
              sales, applicable taxes will be added unless a valid exemption certificate is provided before
              invoicing. You are responsible for any taxes arising from the transfer of your equipment to us
              (if applicable by law).
            </p>
          </section>

          <section id="credit" className="terms-section">
            <h2>6. Purchase Orders & Credit Terms</h2>
            <p>
              We may extend credit at our discretion based on a credit application and trade references and may
              modify or withdraw credit terms at any time. We may require deposits, milestone payments, or
              letters of credit. Issued POs are subject to acceptance.
            </p>
          </section>

          <section id="late" className="terms-section">
            <h2>7. Late Payments & Remedies</h2>
            <p>
              Overdue balances may accrue interest at the lesser of 1.5% per month or the maximum rate
              permitted by law. We may suspend performance, withhold shipment, recover collection costs (including
              reasonable attorneys’ fees), and apply payments to the oldest invoices first.
            </p>
          </section>

          <section id="title" className="terms-section">
            <h2>8. Title & Risk of Loss</h2>
            <ul>
              <li>
                <strong>When we purchase from you.</strong> Unless agreed otherwise in writing, title transfers
                to Company upon execution of a bill of sale and payment, and risk of loss transfers when
                equipment is loaded for transport under our control or upon Company’s physical possession,
                whichever occurs first.
              </li>
              <li>
                <strong>When we sell to you.</strong> Unless otherwise stated (e.g., Incoterms® or F.O.B.
                terms), title transfers upon full payment and risk of loss transfers upon delivery to the
                carrier or your possession, whichever occurs earlier.
              </li>
            </ul>
            <p className="fine-print">
              Note: Parties may set different transfer points by written agreement. These default provisions
              reflect common UCC Article 2 concepts for sales of goods in the U.S.
            </p>
          </section>

          <section id="inspection" className="terms-section">
            <h2>9. Inspection & Acceptance</h2>
            <ul>
              <li>
                <strong>For our purchases.</strong> Our obligation to pay is conditioned on inspection and
                acceptance. We may reject equipment that is materially different from descriptions provided, is
                unsafe, or is subject to undisclosed encumbrances.
              </li>
              <li>
                <strong>For our sales.</strong> You have a reasonable time after delivery to inspect and must
                promptly notify us of any non‑conformity. Use or installation may constitute acceptance.
              </li>
            </ul>
          </section>

          <section id="deposits" className="terms-section">
            <h2>10. Deposits, Changes & Cancellations</h2>
            <p>
              Deposits (if required) secure inventory and scheduling and may be non‑refundable if you cancel
              after we reserve, build, or dispatch. Changes to orders are subject to availability, schedule
              impact, and price adjustments.
            </p>
          </section>

          <section id="offset" className="terms-section">
            <h2>11. Setoff</h2>
            <p>
              We may set off any amounts you owe us against amounts we owe you, whether arising under the same
              or different transactions.
            </p>
          </section>

          <section id="returns" className="terms-section">
            <h2>12. Returns & Refunds (Sales by Company)</h2>
            <p>
              Returns require prior written authorization (RMA). Standard restocking fees and freight policies
              may apply if goods are operational and undamaged. Custom builds, specially procured, or installed
              items are typically non‑returnable.
            </p>
          </section>

          <section id="fm" className="terms-section">
            <h2>13. Force Majeure</h2>
            <p>
              Neither party is liable for delays or failures caused by events beyond reasonable control (e.g.,
              acts of God, labor disputes, shortages, carrier delays, government actions). The affected party
              will notify the other and resume performance as soon as practicable.
            </p>
          </section>

          <section id="compliance" className="terms-section">
            <h2>14. Compliance</h2>
            <p>
              You represent that equipment sold to us is free of liens and was acquired and used in compliance
              with applicable laws. For our sales, you agree to comply with all laws applicable to receipt,
              installation, and use of the goods, including export controls and sanctions.
            </p>
          </section>

          <section id="law" className="terms-section">
            <h2>15. Governing Law & Disputes</h2>
            <p>
              These Terms and any related transactions are governed by the laws of the State where our shipping
              point is located, without regard to its conflict‑of‑laws rules. The U.N. Convention on Contracts
              for the International Sale of Goods does not apply. Exclusive venue lies in the state or federal
              courts in that jurisdiction. Each party waives trial by jury to the extent permitted by law.
            </p>
          </section>

          <section id="changes" className="terms-section">
            <h2>16. Changes to these Terms</h2>
            <p>
              We may update these Terms from time to time. Updates apply to transactions thereafter unless a
              prior written agreement states otherwise.
            </p>
          </section>

          <section id="contact" className="terms-section">
            <h2>17. Payment Instructions & Contact</h2>
            <p>
              Payment instructions appear on our invoices and quotes. To verify bank details (recommended for
              fraud prevention), call the number on our website. For questions, please <Link to="/contact-us">
              contact us</Link>.
            </p>
          </section>
        </article>
      </section>

      {/* Optional global footer */}
      <Footer/>
    </main>
  );
}